<template>
  <div class="csr-page">

    <div class="csr-page-header">
      <h1 class="csr-page-title">Финансовый отчёт</h1>
    </div>

    <q-banner v-if="errorText" rounded class="error-banner q-mb-sm">{{ errorText }}</q-banner>

    <div class="csr-filters-card">
      <div class="csr-filters-card__body">
        <div class="csr-filters-toolbar">
          <div class="csr-filters-main">
            <CustomSelect
              :options="residentOptions"
              option-label-key="label"
              option-value-key="value"
              label="ЖК"
              :value="filterResident"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filterResident"
              class="csr-filter-field"
              @change="(v) => filterResident = v"
            />
            <CustomSelect
              :options="projectTypeOptions"
              option-label-key="label"
              option-value-key="value"
              label="Тип проекта"
              :value="filterProjectType"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filterProjectType"
              class="csr-filter-field"
              @change="(v) => filterProjectType = v"
            />
            <CustomSelect
              :options="contractorOptions"
              option-label-key="label"
              option-value-key="value"
              label="Контрагент"
              :value="filterContractor"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filterContractor"
              class="csr-filter-field"
              @change="(v) => filterContractor = v"
            />
            <CustomSelect
              :options="statusOptions"
              option-label-key="label"
              option-value-key="value"
              label="Статус"
              :value="filterStatus"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filterStatus"
              class="csr-filter-field"
              @change="(v) => filterStatus = v"
            />
          </div>

          <div class="csr-filters-actions">
            <q-btn
              class="csr-filter-action-btn"
              outline
              color="grey-7"
              no-caps
              dense
              icon="filter_alt_off"
              label="Сбросить"
              :disable="!hasActiveFilters"
              @click="resetFilters"
            />
            <q-btn
              class="csr-filter-action-btn"
              color="positive"
              unelevated
              no-caps
              dense
              icon="download"
              label="Excel"
              :disable="!filteredRows.length || loading || exporting"
              :loading="exporting"
              @click="exportExcel"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Статистика + легенда -->
    <div class="stats-row q-mb-sm">
      <div class="stats-row__count">
        Строк: <strong>{{ filteredRows.length }}</strong>
        <span v-if="filteredRows.length !== rows.length" class="stats-row__total-hint">
          из {{ rows.length }}
        </span>
      </div>
      <div class="legend">
        <q-chip
          dense
          clickable
          class="legend-chip legend-chip--green"
          :class="{ 'legend-chip--active': filterPaymentStatus === 'fully_paid' }"
          @click="togglePaymentStatusFilter('fully_paid')"
        >
          <q-icon name="check_circle" size="13px" class="q-mr-xs" />
          Полностью оплачено
          <q-tooltip>Оплачено по акту сверки ≥ сданных работ, нет ожидаемой оплаты</q-tooltip>
        </q-chip>
        <q-chip
          dense
          clickable
          class="legend-chip legend-chip--yellow"
          :class="{ 'legend-chip--active': filterPaymentStatus === 'pending' }"
          @click="togglePaymentStatusFilter('pending')"
        >
          <q-icon name="schedule" size="13px" class="q-mr-xs" />
          Ожидается оплата
          <q-tooltip>Есть непогашенная задолженность по оплате</q-tooltip>
        </q-chip>
      </div>
    </div>

    <!-- Таблица -->
    <CustomTable
      title=""
      :rowsData="filteredRows"
      :columns="columns"
      :row-key="detailRowKey"
      :loading="loading"
      :small="true"
      :custom-body="true"
      :bottom-row="true"
      :sticky-header="true"
    >
      <template #customBody="props">
        <q-tr :class="rowClass(props.row)">

          <CustomTableCell align="center" vertical-align="center">
            <q-btn
              flat
              dense
              round
              size="sm"
              class="detail-toggle-btn"
              :icon="isDetailExpanded(props.row) ? 'expand_less' : 'expand_more'"
              :loading="isDetailLoading(props.row)"
              @click.stop="toggleDetail(props.row)"
            >
              <q-tooltip>{{ isDetailExpanded(props.row) ? 'Скрыть детализацию' : 'Показать детализацию' }}</q-tooltip>
            </q-btn>
          </CustomTableCell>

          <CustomTableCell align="center" vertical-align="center">
            <span class="row-num">{{ props.row.project_id }}</span>
          </CustomTableCell>

          <CustomTableCell align="left" vertical-align="center">
            <div class="cell-jk">
              <q-icon name="apartment" size="13px" color="blue-grey-4" />
              {{ props.row.resident_name || '—' }}
            </div>
          </CustomTableCell>

          <CustomTableCell align="left" vertical-align="center">
            {{ props.row.project_type_name || '—' }}
          </CustomTableCell>

          <!-- Статус проекта -->
          <CustomTableCell align="center" vertical-align="center">
            <q-chip
              dense
              :class="statusChipClass(props.row.project_status_id)"
              class="status-chip-project"
            >
              {{ props.row.project_status_name || '—' }}
              <q-tooltip>Статус проекта</q-tooltip>
            </q-chip>
          </CustomTableCell>

          <CustomTableCell align="left" vertical-align="center">
            <div class="cell-contractor">
              <q-icon
                v-if="props.row.contractor_name === 'Нет Субподряда'"
                name="remove_circle_outline" size="13px" color="grey-4"
              />
              <q-icon v-else name="handshake" size="13px" color="primary" />
              <span :class="{ 'text-grey-5': props.row.contractor_name === 'Нет Субподряда' }" class="q-ml-xs">
                {{ props.row.contractor_name }}
              </span>
            </div>
          </CustomTableCell>

          <CustomTableCell align="left" vertical-align="center">
            <Clickable
              v-if="props.row.agreement_num && props.row.project_id"
              link
              altMode
              hideUnderline
              :href="buildProjectLink(props.row)"
            >
              <span class="agreement-num agreement-num--link">{{ props.row.agreement_num }}</span>
            </Clickable>
            <span v-else class="agreement-num">{{ props.row.agreement_num || '—' }}</span>
          </CustomTableCell>

          <!-- Сумма договора -->
          <CustomTableCell align="right" vertical-align="center">
            <span class="amount">{{ fmt(props.row.agreement_sum) }}</span>
          </CustomTableCell>

          <!-- Сдано работ + прогресс-бар -->
          <CustomTableCell align="right" vertical-align="center">
            <div class="amount-cell">
              <span class="amount">{{ fmt(props.row.done_sum) }}</span>
              <div class="mini-bar">
                <div
                  class="mini-bar__fill mini-bar__fill--done"
                  :style="{ width: pct(props.row.done_sum, props.row.agreement_sum) }"
                >
                  <q-tooltip>{{ pct(props.row.done_sum, props.row.agreement_sum) }} от суммы договора</q-tooltip>
                </div>
              </div>
            </div>
          </CustomTableCell>

          <!-- Оплачено по акту сверки + прогресс-бар -->
          <CustomTableCell align="right" vertical-align="center">
            <div class="amount-cell">
              <span class="amount">{{ fmt(props.row.debit_sum) }}</span>
              <div class="mini-bar">
                <div
                  class="mini-bar__fill mini-bar__fill--paid"
                  :style="{ width: pct(props.row.debit_sum, props.row.done_sum) }"
                >
                  <q-tooltip>{{ pct(props.row.debit_sum, props.row.done_sum) }} от сданных работ</q-tooltip>
                </div>
              </div>
            </div>
          </CustomTableCell>

          <!-- Ожидается оплата -->
          <CustomTableCell align="right" vertical-align="center">
            <div class="expected-payment-cell">
              <div v-if="isZero(props.row.expected_payment_without_warranty)" class="badge badge--ok">
                <q-icon name="check_circle" size="13px" />
                0
              </div>
              <div v-else class="badge badge--warn">
                <q-icon name="schedule" size="13px" />
                {{ fmt(props.row.expected_payment_without_warranty) }}
                <q-tooltip>С учетом удержания гарантии: {{ fmt(props.row.expected_payment_without_warranty) }}</q-tooltip>
              </div>
              <div v-if="props.row.expected_payment_with_warranty > 0" class="expected-payment-info">
                <SvgIcon name="info" size="14px" class="expected-payment-info__icon" />
                <q-popup-proxy>
                  <q-banner class="tw-p-2">
                    <div class="tw-text-sm tw-text-slate-500 tw-mb-1">Без удержания гарантии</div>
                    <div class="tw-font-semibold">{{ fmt(props.row.expected_payment_with_warranty) }}</div>
                  </q-banner>
                </q-popup-proxy>
              </div>
            </div>
          </CustomTableCell>

          <!-- Закрыто по АВР -->
          <CustomTableCell align="right" vertical-align="center">
            <span class="amount">{{ fmt(props.row.credit_sum) }}</span>
          </CustomTableCell>

          <!-- Можно закрыть АВР -->
          <CustomTableCell align="right" vertical-align="center">
            <span class="amount">{{ fmt(props.row.avr_closable_sum) }}</span>
            <q-tooltip>Сумма полностью принятых работ (is_checked), 100% call_sum</q-tooltip>
          </CustomTableCell>

          <!-- Ошибка по платежам -->
          <CustomTableCell align="left" vertical-align="top" class="payment-error-cell">
            <template v-if="paymentErrorText(props.row)">
              <div v-if="!isPaymentErrorExpanded(props.row)" class="payment-error-wrap">
                <div class="payment-error-text payment-error-text--clamped">{{ paymentErrorText(props.row) }}</div>
                <q-btn
                  flat dense no-caps size="sm" class="payment-error-toggle"
                  icon="expand_more" label="Развернуть"
                  @click.stop="togglePaymentError(props.row)"
                />
              </div>
              <div v-else class="payment-error-wrap">
                <div class="payment-error-text payment-error-text--full">{{ paymentErrorText(props.row) }}</div>
                <q-btn
                  flat dense no-caps size="sm" class="payment-error-toggle"
                  icon="expand_less" label="Свернуть"
                  @click.stop="togglePaymentError(props.row)"
                />
              </div>
            </template>
          </CustomTableCell>

        </q-tr>

        <q-tr v-if="isDetailExpanded(props.row)" class="csr-detail-row">
          <q-td colspan="14" class="csr-detail-cell">
            <div class="detail-panel">
              <div v-if="isDetailLoading(props.row)" class="detail-state">
                <q-spinner size="22px" color="primary" />
                <span>Загружаем детализацию...</span>
              </div>

              <q-banner v-else-if="detailError(props.row)" rounded class="detail-error">
                {{ detailError(props.row) }}
              </q-banner>

              <template v-else-if="detailFor(props.row)">
                <div class="detail-hero">
                  <div class="detail-hero-main">
                    <div class="detail-eyebrow">Диагностика расхождения</div>
                    <div class="detail-title">
                      {{ props.row.resident_name }} / {{ props.row.project_type_name }} / {{ props.row.contractor_name }}
                    </div>
                    <div class="detail-formula">
                      <span>{{ fmt(detailSummary(props.row).report_current_smeta_sum) }}</span>
                      <q-icon name="remove" size="16px" />
                      <span>{{ fmt(detailSummary(props.row).buh_avr_docs_sum) }}</span>
                      <q-icon name="drag_handle" size="16px" />
                      <strong>{{ fmt(detailSummary(props.row).delta_current_minus_buh_avr) }}</strong>
                    </div>
                    <div class="detail-hero-note">
                      Разница между текущей сметой и закрытым АВР. После удержания гарантии {{ detailSummary(props.row).warranty || 0 }}%: <b>{{ fmt(detailSummary(props.row).delta_after_warranty) }}</b>
                    </div>
                  </div>
                  <div class="detail-hero-side">
                    <div class="detail-hero-side__label">Закрыто АВР</div>
                    <strong>{{ detailSummary(props.row).buh_avr_docs_count || 0 }}</strong>
                    <span>{{ fmt(detailSummary(props.row).buh_avr_docs_sum) }}</span>
                  </div>
                </div>

                <div class="detail-summary-grid">
                  <div class="detail-summary-card">
                    <q-icon name="calculate" size="18px" />
                    <span>Текущая смета</span>
                    <strong>{{ fmt(detailSummary(props.row).report_current_smeta_sum) }}</strong>
                  </div>
                  <div class="detail-summary-card">
                    <q-icon name="fact_check" size="18px" />
                    <span>Закрыто в вызовах</span>
                    <strong>{{ fmt(detailSummary(props.row).frozen_call_sum) }}</strong>
                  </div>
                  <div class="detail-summary-card detail-summary-card--accent">
                    <q-icon name="trending_up" size="18px" />
                    <span>Разница смета / АВР</span>
                    <strong>{{ fmt(detailSummary(props.row).delta_current_minus_buh_avr) }}</strong>
                  </div>
                  <div class="detail-summary-card">
                    <q-icon name="verified" size="18px" />
                    <span>После гарантии {{ detailSummary(props.row).warranty || 0 }}%</span>
                    <strong>{{ fmt(detailSummary(props.row).delta_after_warranty) }}</strong>
                  </div>
                </div>

                <div class="detail-payment-breakdown">
                  <div class="detail-payment-breakdown__head">
                    <div>
                      <div class="detail-section-title">Почему “Ожидается оплата” может отличаться</div>
                      <div class="detail-muted">Это сумма после гарантии минус фактически оплачено по акту сверки.</div>
                    </div>
                    <div class="detail-payment-breakdown__total">
                      <span>Ожидается оплата</span>
                      <strong>{{ fmt(detailPaymentBreakdown(props.row).expected) }}</strong>
                    </div>
                  </div>
                  <div class="detail-payment-breakdown__grid">
                    <div class="detail-payment-item detail-payment-item--blue">
                      <span>Незакрытая смета после гарантии</span>
                      <strong>{{ fmt(detailPaymentBreakdown(props.row).unclosedAfterWarranty) }}</strong>
                    </div>
                    <div class="detail-payment-item detail-payment-item--green">
                      <span>{{ detailPaymentBreakdown(props.row).closedUnpaid >= 0 ? 'Недоплата / округление по закрытым АВР' : 'Переплата по закрытым АВР' }}</span>
                      <strong>{{ fmt(detailPaymentBreakdown(props.row).closedUnpaid) }}</strong>
                    </div>
                    <div class="detail-payment-item">
                      <span>Оплачено по акту сверки</span>
                      <strong>{{ fmt(detailPaymentBreakdown(props.row).debit) }}</strong>
                    </div>
                  </div>
                </div>

                <div class="detail-section">
                  <div class="detail-section-title detail-section-title--row">
                    <span>Причины</span>
                    <div class="detail-section-actions">
                      <span class="detail-balance">
                        Объяснено: {{ fmt(detailReasonsDelta(props.row)) }} из {{ fmt(detailSummary(props.row).delta_current_minus_buh_avr) }}
                      </span>
                      <q-btn
                        flat
                        dense
                        no-caps
                        size="sm"
                        class="detail-clear-filter"
                        label="Показать все"
                        :disable="!detailReasonFilter[detailRowKey(props.row)]"
                        @click.stop="setDetailReasonFilter(props.row, null)"
                      />
                    </div>
                  </div>
                  <div class="detail-reasons">
                    <button
                      v-for="reason in detailReasonGroups(props.row)"
                      :key="reason.reason_code"
                      type="button"
                      class="detail-reason-card"
                      :class="[reasonClass(reason.reason_code), { 'detail-reason-card--active': isDetailReasonSelected(props.row, reason.reason_code) }]"
                      @click.stop="setDetailReasonFilter(props.row, reason.reason_code)"
                    >
                      <div class="detail-reason-top">
                        <q-icon :name="reasonIcon(reason.reason_code)" size="18px" />
                        <span>{{ shortReason(reason.reason_code) }}</span>
                      </div>
                      <div class="detail-reason-name">{{ reason.reason_name }}</div>
                      <div class="detail-reason-meta">
                        <span>{{ reason.rows_count }} строк</span>
                        <strong>{{ fmt(reason.delta) }}</strong>
                      </div>
                    </button>
                  </div>
                </div>

                <div class="detail-section">
                  <div class="detail-section-title detail-section-title--row">
                    <span>Детальные строки</span>
                    <div class="detail-section-actions">
                      <span class="detail-muted">{{ detailVisibleWorkRows(props.row).length }} из {{ detailWorkRows(props.row).length }} строк</span>
                      <q-btn
                        flat
                        dense
                        no-caps
                        size="sm"
                        class="detail-clear-filter"
                        label="Сбросить фильтры строк"
                        :disable="!hasDetailDataFilters(props.row)"
                        @click.stop="clearDetailDataFilters(props.row)"
                      />
                    </div>
                  </div>
                  <div class="detail-row-filters" @click.stop @mousedown.stop>
                    <q-btn
                      v-for="filter in detailFilterConfigs"
                      :key="filter.field"
                      no-caps
                      unelevated
                      class="detail-row-filter-btn"
                      :class="{ 'detail-row-filter-btn--active': detailFilterValue(props.row, filter.field).length }"
                      :label="detailFilterButtonLabel(props.row, filter)"
                      icon-right="expand_more"
                    >
                      <q-menu class="detail-filter-menu" content-class="detail-filter-menu-content">
                        <q-list dense separator class="detail-filter-list">
                          <q-item
                            v-for="option in detailFilterOptions(props.row, filter.source)"
                            :key="option.value"
                            clickable
                            class="detail-filter-option"
                            :class="{ 'detail-filter-option--active': isDetailFilterOptionSelected(props.row, filter.field, option.value) }"
                            @click.stop="toggleDetailFilterOption(props.row, filter.field, option.value)"
                          >
                            <q-item-section side>
                              <q-icon
                                :name="isDetailFilterOptionSelected(props.row, filter.field, option.value) ? 'check_box' : 'check_box_outline_blank'"
                                size="18px"
                              />
                            </q-item-section>
                            <q-item-section>
                              <span>{{ option.label }}</span>
                            </q-item-section>
                          </q-item>
                          <q-item v-if="!detailFilterOptions(props.row, filter.source).length" class="detail-filter-option detail-filter-option--empty">
                            <q-item-section>Нет вариантов</q-item-section>
                          </q-item>
                        </q-list>
                      </q-menu>
                    </q-btn>
                  </div>
                  <div class="detail-table-wrap">
                    <table class="detail-table">
                      <thead>
                        <tr>
                          <th>Блок</th>
                          <th>Этаж</th>
                          <th>Группа</th>
                          <th>Работа</th>
                          <th>Объем</th>
                          <th>Сумма в АВР</th>
                          <th>Сумма сейчас</th>
                          <th>Разница</th>
                          <th>Причина</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!detailVisibleWorkRowsLimited(props.row).length">
                          <td colspan="9" class="detail-empty-filter">
                            По выбранным фильтрам строк не найдено
                          </td>
                        </tr>
                        <tr v-for="item in detailVisibleWorkRowsLimited(props.row)" :key="detailWorkRowKey(item)">
                          <td>{{ item.block_name || '—' }}</td>
                          <td>{{ item.level_num || '—' }}</td>
                          <td>{{ item.work_set_check_group_name || '—' }}</td>
                          <td>
                            <div class="detail-work-name">{{ item.work_set_name || '—' }}</div>
                            <div v-if="item.help_call_id || item.remont_work_set_id" class="detail-work-meta">
                              help_call_id: {{ item.help_call_id }}, remont_work_set_id: {{ item.remont_work_set_id }}
                            </div>
                          </td>
                          <td>
                            <div v-if="hasVolumeInfo(item)" class="detail-volume">
                              <div><span>АВР</span><strong>{{ fmtAmount(item.call_amount) }} {{ item.unit_short_name || '' }}</strong></div>
                              <div><span>Сейчас</span><strong>{{ fmtAmount(item.current_amount) }} {{ item.unit_short_name || '' }}</strong></div>
                              <div class="detail-volume__delta" :class="{ 'detail-volume__delta--negative': Number(displayAmountDelta(item)) < 0 }">
                                <span>Δ</span><strong>{{ fmtAmountSigned(displayAmountDelta(item)) }} {{ item.unit_short_name || '' }}</strong>
                              </div>
                            </div>
                            <span v-else class="detail-muted">—</span>
                          </td>
                          <td class="text-right">{{ fmt(item.call_sum) }}</td>
                          <td class="text-right">{{ fmt(item.current_total_sum) }}</td>
                          <td class="text-right detail-delta">{{ fmt(displayMoneyDelta(item)) }}</td>
                          <td>
                            <q-chip dense class="detail-reason-chip" :class="reasonClass(item.reason_code)">
                              {{ shortReason(item.reason_code) }}
                            </q-chip>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="hasHiddenDetailRows(props.row)" class="detail-muted detail-hidden-note">
                    Показаны первые {{ maxDetailRows }} строк из {{ detailVisibleWorkRows(props.row).length }}.
                  </div>
                </div>

                <div class="detail-section">
                  <div class="detail-section-title">Документы АВР</div>
                  <div class="detail-docs">
                    <div v-for="doc in detailDocuments(props.row)" :key="doc.floor_map_document_id" class="detail-doc-card">
                      <div class="detail-doc-title">{{ doc.doc_type_name || doc.doc_type_code }} #{{ doc.floor_map_document_id }}</div>
                      <div class="detail-doc-meta">{{ doc.doc_create_date || '—' }}</div>
                      <strong>{{ fmt(doc.avr_sum) }}</strong>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </q-td>
        </q-tr>
      </template>

      <!-- Итоговая строка -->
      <template #bottomRow>
        <q-tr class="csr-total-row">
          <q-td colspan="7" class="csr-total-row__label">Итого ({{ filteredRows.length }} строк)</q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('agreement_sum')) }}</q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('done_sum')) }}</q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('debit_sum')) }}</q-td>
          <q-td class="csr-total-row__cell text-right">
            <div v-if="isZero(sum('expected_payment_without_warranty'))" class="badge badge--ok">
              <q-icon name="check_circle" size="13px" /> 0
            </div>
            <div v-else class="badge badge--warn">{{ fmt(sum('expected_payment_without_warranty')) }}</div>
          </q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('credit_sum')) }}</q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('avr_closable_sum')) }}</q-td>
          <q-td class="csr-total-row__cell csr-total-row__cell--empty" />
        </q-tr>
      </template>
    </CustomTable>

  </div>
</template>

<script>
import instance from "@/services/api";
import CustomSelect from "@/components/form/CustomSelect";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import Clickable from "@/components/common/Clickable.vue";
import SvgIcon from "@/components/common/SvgIcon";

const STATUS_META = {
  100: { label: "Создан",    cls: "status--created"   },
  200: { label: "Старт СМР", cls: "status--active"    },
  300: { label: "Завершен",  cls: "status--done"      },
  400: { label: "Отменен",   cls: "status--cancelled" },
};

const numSort = (a, b) => (Number(a) || 0) - (Number(b) || 0);

const columns = [
  { name: "detail",             label: "",                   field: "detail",             align: "center", style: "width:44px" },
  { name: "project_id",         label: "ID",                 field: "project_id",         align: "center", style: "width:52px", sortable: true, sort: numSort },
  { name: "resident_name",      label: "ЖК",                field: "resident_name",      align: "left", sortable: true },
  { name: "project_type_name",  label: "Тип проекта",       field: "project_type_name",  align: "left", sortable: true },
  { name: "project_status_name",label: "Статус",            field: "project_status_name",align: "center", sortable: true },
  { name: "contractor_name",    label: "Контрагент",        field: "contractor_name",    align: "left", sortable: true },
  { name: "agreement_num",      label: "Договор",           field: "agreement_num",      align: "left", sortable: true },
  { name: "agreement_sum",      label: "Сумма договора",    field: "agreement_sum",      align: "right", sortable: true, sort: numSort },
  { name: "done_sum",           label: "Сдано работ",       field: "done_sum",           align: "right", sortable: true, sort: numSort },
  { name: "debit_sum",          label: "Оплачено по акту сверки", field: "debit_sum",          align: "right", sortable: true, sort: numSort },
  { name: "expected_payment_without_warranty",   label: "Ожидается оплата",  field: "expected_payment_without_warranty",   align: "right", sortable: true, sort: numSort },
  { name: "credit_sum",         label: "Закрыто по АВР",       field: "credit_sum",         align: "right", sortable: true, sort: numSort },
  { name: "avr_closable_sum",   label: "Можно закрыть АВР", field: "avr_closable_sum",   align: "right", sortable: true, sort: numSort },
  { name: "payment_error_info", label: "Ошибка по платежам",  field: "payment_error_info", align: "left", style: "min-width:160px;max-width:320px", sortable: true },
];

const DETAIL_FILTER_CONFIGS = [
  { field: "blocks", label: "Блок", source: "block_name" },
  { field: "floors", label: "Этаж", source: "level_num" },
  { field: "groups", label: "Группа", source: "work_set_check_group_name" },
  { field: "works", label: "Работа", source: "work_set_name" },
];

export default {
  name: "ContractorSbsReport",
  components: { CustomSelect, CustomTable, CustomTableCell, Clickable, SvgIcon },
  data() {
    return {
      rows: [],
      columns,
      loading: false,
      exporting: false,
      errorText: "",
      filterResident: null,
      filterProjectType: null,
      filterContractor: null,
      filterStatus: null,
      filterPaymentStatus: null,
      paymentErrorExpanded: {},
      detailExpanded: {},
      detailLoading: {},
      detailErrors: {},
      detailData: {},
      detailReasonFilter: {},
      detailDataFilters: {},
      detailFilterConfigs: DETAIL_FILTER_CONFIGS,
      maxDetailRows: 120,
    };
  },
  computed: {
    residentOptions() {
      const seen = new Set();
      return this.rows
        .filter((r) => r.resident_name && !seen.has(r.resident_name) && seen.add(r.resident_name))
        .map((r) => ({ label: r.resident_name, value: r.resident_name }));
    },
    projectTypeOptions() {
      const seen = new Set();
      return this.rows
        .filter((r) => r.project_type_name && !seen.has(r.project_type_name) && seen.add(r.project_type_name))
        .map((r) => ({ label: r.project_type_name, value: r.project_type_name }));
    },
    contractorOptions() {
      const seen = new Set();
      return this.rows
        .filter((r) => r.contractor_name && !seen.has(r.contractor_name) && seen.add(r.contractor_name))
        .map((r) => ({ label: r.contractor_name, value: r.contractor_name }));
    },
    statusOptions() {
      const seen = new Set();
      return this.rows
        .filter((r) => r.project_status_name && !seen.has(r.project_status_name) && seen.add(r.project_status_name))
        .map((r) => ({ label: r.project_status_name, value: r.project_status_name }));
    },
    hasActiveFilters() {
      return !!(
        this.filterResident
        || this.filterProjectType
        || this.filterContractor
        || this.filterStatus
        || this.filterPaymentStatus
      );
    },
    filteredRows() {
      return this.rows.filter((r) => {
        if (this.filterResident && r.resident_name !== this.filterResident) return false;
        if (this.filterProjectType && r.project_type_name !== this.filterProjectType) return false;
        if (this.filterContractor && r.contractor_name !== this.filterContractor) return false;
        if (this.filterStatus && r.project_status_name !== this.filterStatus) return false;
        if (this.filterPaymentStatus === "fully_paid" && !this.isFullyPaidRow(r)) return false;
        if (this.filterPaymentStatus === "pending" && !this.isPendingPaymentRow(r)) return false;
        return true;
      });
    },
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    fmt(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      if (n === 0) return "0";
      return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtAmount(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      if (n === 0) return "0";
      return n.toLocaleString("ru-RU", { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    },
    fmtAmountSigned(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      if (n === 0) return "0";
      const sign = n > 0 ? "+" : "";
      return `${sign}${this.fmtAmount(n)}`;
    },
    hasNumericValue(value) {
      return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
    },
    displayAmountDelta(item) {
      if (!item) return null;
      return this.hasNumericValue(item.amount_delta_display) ? item.amount_delta_display : item.amount_delta;
    },
    displayMoneyDelta(item) {
      if (!item) return null;
      return this.hasNumericValue(item.delta_display) ? item.delta_display : item.delta;
    },
    hasVolumeInfo(item) {
      return item
        && (this.hasNumericValue(item.call_amount)
          || this.hasNumericValue(item.current_amount)
          || this.hasNumericValue(this.displayAmountDelta(item)));
    },
    isZero(value) {
      const n = Number(value);
      return Number.isFinite(n) && n === 0;
    },
    pct(part, total) {
      const p = Number(part);
      const t = Number(total);
      if (!t || !Number.isFinite(p) || !Number.isFinite(t)) return "0%";
      return `${Math.min(100, Math.round((p / t) * 100))}%`;
    },
    sum(field) {
      return this.filteredRows.reduce((acc, r) => acc + (Number(r[field]) || 0), 0);
    },
    rowClass(row) {
      if (this.isFullyPaidRow(row)) return "csr-tr--fully-paid";
      if (this.isPendingPaymentRow(row)) return "csr-tr--pending";
      return "";
    },
    isFullyPaidRow(row) {
      const debit = Number(row?.debit_sum) || 0;
      const done = Number(row?.done_sum) || 0;
      const exp = Number(row?.expected_payment_without_warranty) || 0;
      return debit >= done && exp === 0;
    },
    isPendingPaymentRow(row) {
      const exp = Number(row?.expected_payment_without_warranty) || 0;
      return exp > 0;
    },
    togglePaymentStatusFilter(status) {
      this.filterPaymentStatus = this.filterPaymentStatus === status ? null : status;
    },
    statusChipClass(statusId) {
      return STATUS_META[statusId]?.cls || "status--default";
    },
    buildProjectLink(row) {
      const params = new URLSearchParams();
      if (row.resident_id)         params.set("resident_id", row.resident_id);
      if (row.project_type_id)     params.set("project_type_id", row.project_type_id);
      if (row.project_entrance_id) params.set("project_entrance_id", row.project_entrance_id);
      if (row.project_id)          params.set("project_id", row.project_id);
      if (row.sbs_project_id)      params.set("sbs_project_id", row.sbs_project_id);
      params.set("tab", "M__ProjectFormRemontCostTab");
      return `/residential-settings?${params.toString()}`;
    },
    resetFilters() {
      this.filterResident = null;
      this.filterProjectType = null;
      this.filterContractor = null;
      this.filterStatus = null;
      this.filterPaymentStatus = null;
    },
    buildExportParams() {
      const params = {};
      if (this.filterResident) params.resident_name = this.filterResident;
      if (this.filterProjectType) params.project_type_name = this.filterProjectType;
      if (this.filterContractor) params.contractor_name = this.filterContractor;
      if (this.filterStatus) params.project_status_name = this.filterStatus;
      return params;
    },
    buildExportFileName() {
      const date = new Date().toLocaleDateString("ru-RU");
      if (this.filterResident) {
        const safeName = String(this.filterResident).replace(/[\\/:*?"<>|]/g, "_");
        return `Финансовый_отчёт_${safeName}_${date}.xlsx`;
      }
      return `Финансовый_отчёт_${date}.xlsx`;
    },
    async exportExcel() {
      if (!this.filteredRows.length || this.exporting) return;
      this.exporting = true;
      try {
        const blob = await instance()
          .get("/finance_payment/contractor-sbs-report/export/", {
            params: this.buildExportParams(),
            responseType: "blob",
          })
          .then((r) => r?.data);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.buildExportFileName();
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || "Ошибка экспорта";
      } finally {
        this.exporting = false;
      }
    },
    paymentErrorText(row) {
      const t = row?.payment_error_info;
      if (t == null) return "";
      const s = String(t).trim();
      return s;
    },
    paymentErrorRowKey(row) {
      return [row?.project_id, row?.sbs_project_id, row?.agreement_num].join("|");
    },
    isPaymentErrorExpanded(row) {
      return !!this.paymentErrorExpanded[this.paymentErrorRowKey(row)];
    },
    togglePaymentError(row) {
      const k = this.paymentErrorRowKey(row);
      const next = !this.paymentErrorExpanded[k];
      this.paymentErrorExpanded = { ...this.paymentErrorExpanded, [k]: next };
    },
    detailRowKey(row) {
      return [row?.project_id, row?.contractor_agreement_list_id || row?.agreement_num || ""].join("|");
    },
    isDetailExpanded(row) {
      return !!this.detailExpanded[this.detailRowKey(row)];
    },
    isDetailLoading(row) {
      return !!this.detailLoading[this.detailRowKey(row)];
    },
    detailError(row) {
      return this.detailErrors[this.detailRowKey(row)] || "";
    },
    detailFor(row) {
      return this.detailData[this.detailRowKey(row)] || null;
    },
    normalizeJson(value, fallback) {
      if (value == null) return fallback;
      if (typeof value === "string") {
        try { return JSON.parse(value); } catch (e) { return fallback; }
      }
      return value;
    },
    detailSummary(row) {
      return this.normalizeJson(this.detailFor(row)?.summary, {}) || {};
    },
    detailReasonGroups(row) {
      const items = this.normalizeJson(this.detailFor(row)?.reason_groups, []);
      return Array.isArray(items) ? items : [];
    },
    detailReasonsDelta(row) {
      return this.detailReasonGroups(row).reduce((acc, item) => acc + (Number(item?.delta) || 0), 0);
    },
    detailPaymentBreakdown(row) {
      const summary = this.detailSummary(row);
      const warrantyRate = 1 - ((Number(summary.warranty) || 0) / 100);
      const unclosedAfterWarranty = Number(summary.delta_after_warranty) || 0;
      const debit = Number(summary.debit_sum ?? row?.debit_sum) || 0;
      const expected = Number(
        row?.expected_payment_without_warranty
        ?? summary.expected_payment_by_detail
        ?? 0
      ) || 0;

      const closedUnpaidFromSummary = summary.unpaid_closed_avr_after_warranty;
      const closedUnpaid = closedUnpaidFromSummary !== undefined && closedUnpaidFromSummary !== null
        ? Number(closedUnpaidFromSummary) || 0
        : expected - unclosedAfterWarranty;

      const closedAvrAfterWarranty = summary.buh_avr_docs_after_warranty !== undefined && summary.buh_avr_docs_after_warranty !== null
        ? Number(summary.buh_avr_docs_after_warranty) || 0
        : (Number(summary.buh_avr_docs_sum) || 0) * warrantyRate;

      return {
        expected,
        unclosedAfterWarranty,
        closedUnpaid,
        closedAvrAfterWarranty,
        debit,
      };
    },
    detailWorkRows(row) {
      const items = this.normalizeJson(this.detailFor(row)?.work_rows, []);
      return Array.isArray(items) ? items : [];
    },
    detailVisibleWorkRows(row) {
      const selectedReason = this.detailReasonFilter[this.detailRowKey(row)];
      const filters = this.detailFilterState(row);
      let rows = this.detailWorkRows(row);
      if (selectedReason) rows = rows.filter((item) => item.reason_code === selectedReason);
      return rows.filter((item) => this.matchesDetailDataFilters(item, filters));
    },
    detailVisibleWorkRowsLimited(row) {
      return this.detailVisibleWorkRows(row).slice(0, this.maxDetailRows);
    },
    detailWorkRowsLimited(row) {
      return this.detailVisibleWorkRowsLimited(row);
    },
    hasHiddenDetailRows(row) {
      return this.detailVisibleWorkRows(row).length > this.maxDetailRows;
    },
    setDetailReasonFilter(row, reasonCode) {
      const key = this.detailRowKey(row);
      this.detailReasonFilter = { ...this.detailReasonFilter, [key]: reasonCode };
    },
    isDetailReasonSelected(row, reasonCode) {
      return this.detailReasonFilter[this.detailRowKey(row)] === reasonCode;
    },
    detailFilterState(row) {
      return this.detailDataFilters[this.detailRowKey(row)] || {};
    },
    detailFilterValue(row, field) {
      const value = this.detailFilterState(row)[field];
      return Array.isArray(value) ? value : [];
    },
    normalizeDetailFilterValue(value) {
      if (value == null) return [];
      return Array.isArray(value) ? value : [value];
    },
    setDetailFilter(row, field, value) {
      const key = this.detailRowKey(row);
      const nextState = {
        ...this.detailFilterState(row),
        [field]: this.normalizeDetailFilterValue(value),
      };
      this.detailDataFilters = { ...this.detailDataFilters, [key]: nextState };
    },
    toggleDetailFilterOption(row, field, value) {
      const current = this.detailFilterValue(row, field);
      const normalizedValue = String(value);
      const next = current.includes(normalizedValue)
        ? current.filter((item) => item !== normalizedValue)
        : [...current, normalizedValue];
      this.setDetailFilter(row, field, next);
    },
    isDetailFilterOptionSelected(row, field, value) {
      return this.detailFilterValue(row, field).includes(String(value));
    },
    detailFilterButtonLabel(row, filter) {
      const selected = this.detailFilterValue(row, filter.field);
      if (!selected.length) return filter.label;
      if (selected.length === 1) return `${filter.label}: ${selected[0]}`;
      return `${filter.label}: ${selected.length}`;
    },
    clearDetailDataFilters(row) {
      const key = this.detailRowKey(row);
      this.detailDataFilters = {
        ...this.detailDataFilters,
        [key]: { blocks: [], floors: [], groups: [], works: [] },
      };
    },
    hasDetailDataFilters(row) {
      const filters = this.detailFilterState(row);
      return ["blocks", "floors", "groups", "works"].some((field) => Array.isArray(filters[field]) && filters[field].length);
    },
    detailFilterOptions(row, sourceField) {
      const values = new Map();
      this.detailWorkRows(row).forEach((item) => {
        if (item?.reason_code === "FROZEN_CALL_SUM_DIFF_AVR") return;
        const value = item?.[sourceField];
        if (value === null || value === undefined || value === "") return;
        const key = String(value);
        if (!values.has(key)) values.set(key, value);
      });
      return Array.from(values.values())
        .sort((a, b) => this.compareDetailFilterValues(a, b))
        .map((value) => ({ label: String(value), value: String(value) }));
    },
    compareDetailFilterValues(a, b) {
      const an = Number(a);
      const bn = Number(b);
      if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
      return String(a).localeCompare(String(b), "ru");
    },
    matchesDetailDataFilters(item, filters) {
      const checks = [
        ["blocks", item?.block_name],
        ["floors", item?.level_num],
        ["groups", item?.work_set_check_group_name],
        ["works", item?.work_set_name],
      ];
      return checks.every(([field, value]) => {
        const selected = Array.isArray(filters[field]) ? filters[field] : [];
        if (!selected.length) return true;
        return selected.includes(String(value));
      });
    },
    detailDocuments(row) {
      const items = this.normalizeJson(this.detailFor(row)?.documents, []);
      return Array.isArray(items) ? items : [];
    },
    detailWorkRowKey(item) {
      return [item.reason_code, item.help_call_id, item.check_group_call_id, item.remont_work_set_id].join("|");
    },
    reasonIcon(reasonCode) {
      if (reasonCode === "AMOUNT_CHANGED_AFTER_AVR") return "swap_vert";
      if (reasonCode === "NEW_SMETA_ROW_NOT_IN_AVR") return "add_circle_outline";
      if (reasonCode === "CALL_ROW_NOT_IN_CURRENT_SMETA") return "remove_circle_outline";
      if (reasonCode === "FROZEN_CALL_SUM_DIFF_AVR") return "receipt_long";
      return "info";
    },
    reasonClass(reasonCode) {
      if (reasonCode === "AMOUNT_CHANGED_AFTER_AVR") return "detail-reason--changed";
      if (reasonCode === "NEW_SMETA_ROW_NOT_IN_AVR") return "detail-reason--new";
      if (reasonCode === "CALL_ROW_NOT_IN_CURRENT_SMETA") return "detail-reason--missing";
      if (reasonCode === "FROZEN_CALL_SUM_DIFF_AVR") return "detail-reason--docs";
      return "detail-reason--default";
    },
    shortReason(reasonCode) {
      if (reasonCode === "AMOUNT_CHANGED_AFTER_AVR") return "Изменилась сумма";
      if (reasonCode === "NEW_SMETA_ROW_NOT_IN_AVR") return "Не было в АВР";
      if (reasonCode === "CALL_ROW_NOT_IN_CURRENT_SMETA") return "Убрано из сметы";
      if (reasonCode === "FROZEN_CALL_SUM_DIFF_AVR") return "Вызовы / АВР";
      return "Без расхождения";
    },
    async toggleDetail(row) {
      const key = this.detailRowKey(row);
      const next = !this.detailExpanded[key];
      this.detailExpanded = { ...this.detailExpanded, [key]: next };
      if (next && !this.detailData[key] && !this.detailLoading[key]) {
        await this.fetchDetail(row);
      }
    },
    async fetchDetail(row) {
      const key = this.detailRowKey(row);
      this.detailLoading = { ...this.detailLoading, [key]: true };
      this.detailErrors = { ...this.detailErrors, [key]: "" };
      try {
        const params = { project_id: row.project_id };
        if (row.contractor_agreement_list_id) {
          params.contractor_agreement_list_id = row.contractor_agreement_list_id;
        }
        const payload = await instance()
          .get("/finance_payment/contractor-sbs-report/detail/", { params })
          .then((r) => r?.data);
        const detail = payload?.data || null;
        if (!detail) throw new Error("Детализация не найдена");
        this.detailData = { ...this.detailData, [key]: detail };
      } catch (e) {
        const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || "Ошибка загрузки детализации";
        this.detailErrors = { ...this.detailErrors, [key]: msg };
      } finally {
        this.detailLoading = { ...this.detailLoading, [key]: false };
      }
    },
    async fetchData() {
      this.loading = true;
      this.errorText = "";
      try {
        const res = await instance()
          .get("/finance_payment/contractor-sbs-report/read/")
          .then((r) => r?.data);
        this.rows = res?.data || [];
        this.paymentErrorExpanded = {};
        this.detailExpanded = {};
        this.detailLoading = {};
        this.detailErrors = {};
        this.detailData = {};
        this.detailReasonFilter = {};
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || "Ошибка загрузки данных";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.csr-page {
  padding: 8px 12px 48px;
  max-width: 100%;
  box-sizing: border-box;
}

.csr-page-header {
  margin-bottom: 8px;
}

.csr-page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.error-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #78350f;
  font-size: 13px;
}

.csr-filters-card {
  margin-bottom: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.csr-filters-card__body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.csr-filters-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.csr-filters-main {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.csr-filters-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.csr-filter-field {
  min-width: 0;
  width: 100%;

  :deep(.custom-select__wrapper) {
    width: 100%;
  }

  :deep(.q-field) {
    width: 100%;
  }

  :deep(.q-field__control) {
    min-height: 40px;
  }

  :deep(.q-field__native) {
    min-height: 24px;
  }
}

.csr-filter-action-btn {
  font-size: 12px;
  min-height: 36px;
  width: 118px;
  min-width: 118px;
  padding: 0 8px;
}

@media (max-width: 1280px) {
  .csr-filters-toolbar {
    grid-template-columns: 1fr;
  }

  .csr-filters-main {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .csr-filters-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 720px) {
  .csr-filters-main {
    grid-template-columns: 1fr;
  }

  .csr-filters-actions {
    width: 100%;
  }

  .csr-filter-action-btn {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
  }
}

/* Статистика + легенда */
.stats-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.stats-row__count {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
}

.stats-row__total-hint {
  font-weight: 400;
  color: #94a3b8;
  margin-left: 4px;
}

.legend {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.legend-chip {
  font-size: 11px !important;
  font-weight: 500 !important;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &--green { background: #dcfce7 !important; color: #15803d !important; }
  &--yellow { background: #fef3c7 !important; color: #b45309 !important; }
  &--blue { background: #dbeafe !important; color: #1d4ed8 !important; }

  &--active {
    box-shadow: inset 0 0 0 2px currentColor;
  }
}

/* Цвета строк */
:deep(.csr-tr--fully-paid td) {
  background: #f0fdf4 !important;
  border-left: 3px solid #22c55e;
}
:deep(.csr-tr--pending td) {
  background: #fffbeb !important;
  border-left: 3px solid #f59e0b;
}
:deep(.csr-tr--avr td) {
  background: #eff6ff !important;
  border-left: 3px solid #3b82f6;
}

/* Номер строки */
.row-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f1f5f9;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

/* ЖК */
.cell-jk {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
}

/* Контрагент */
.cell-contractor {
  display: flex;
  align-items: center;
}

/* Статус проекта */
.status-chip-project {
  font-size: 11px !important;
  font-weight: 600 !important;
  padding: 0 8px !important;
  height: 20px !important;
}

:deep(.status--created)   { background: #f1f5f9 !important; color: #475569 !important; }
:deep(.status--active)    { background: #dcfce7 !important; color: #15803d !important; }
:deep(.status--done)      { background: #dbeafe !important; color: #1d4ed8 !important; }
:deep(.status--cancelled) { background: #fee2e2 !important; color: #b91c1c !important; }
:deep(.status--default)   { background: #f1f5f9 !important; color: #94a3b8 !important; }

/* Номер договора */
.agreement-num {
  font-family: monospace;
  font-size: 11px;
  background: #f8fafc;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  white-space: nowrap;
}

.agreement-num--link {
  color: #1d4ed8;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
  }
}

/* Суммы */
.amount {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
  white-space: nowrap;
}

.amount-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}

/* Мини-бар */
.mini-bar {
  width: 60px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 99px;
  overflow: hidden;
}

.mini-bar__fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.35s ease;

  &--done { background: linear-gradient(90deg, #6366f1, #8b5cf6); }
  &--paid { background: linear-gradient(90deg, #10b981, #22c55e); }
}

/* Ожидаемая оплата с info */
.expected-payment-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.expected-payment-info {
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.expected-payment-info__icon {
  color: #64748b;
  transition: color 0.15s;
}

.expected-payment-info:hover .expected-payment-info__icon {
  color: #0f172a;
}

/* Бэджи */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &--ok   { background: #dcfce7; color: #15803d; }
  &--warn { background: #fef3c7; color: #b45309; }
  &--info { background: #dbeafe; color: #1d4ed8; }
}

/* Итоговая строка */
:deep(.csr-total-row) {
  position: sticky;
  bottom: 0;
  z-index: 4;
}

:deep(.csr-total-row td) {
  background: #f1f5f9 !important;
  color: #1e293b !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  border-top: 2px solid #cbd5e1 !important;
}

.csr-total-row__label {
  color: #64748b !important;
  font-size: 11px !important;
  font-weight: 600 !important;
}

.csr-total-row__cell {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
  white-space: nowrap;
}

.csr-total-row__cell--empty {
  border-top: 2px solid #cbd5e1 !important;
}

/* Ошибка по платежам */
.payment-error-cell {
  font-size: 11px;
  color: #475569;
}

.payment-error-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.payment-error-text {
  word-break: break-word;
  line-height: 1.35;
}

.payment-error-text--clamped {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.payment-error-text--full {
  white-space: pre-wrap;
  max-height: 280px;
  overflow: auto;
}

.payment-error-toggle {
  margin-top: 2px;
  font-size: 11px !important;
  min-height: 26px !important;
  padding: 0 6px !important;
}

.detail-toggle-btn {
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.15s ease;

  &:hover {
    color: #1d4ed8;
    background: #eff6ff;
    border-color: #bfdbfe;
  }
}

.csr-detail-row td {
  background: #f8fafc !important;
  border-left: 3px solid #60a5fa;
  border-bottom: 1px solid #dbeafe;
}

.csr-detail-cell {
  padding: 0 !important;
}

.detail-panel {
  margin: 10px 12px 14px;
  padding: 14px;
  border: 1px solid #dbeafe;
  border-radius: 14px;
  background:
    linear-gradient(135deg, rgba(239, 246, 255, 0.9) 0%, rgba(255, 255, 255, 0.96) 42%),
    #fff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.07);
}

.detail-state {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 72px;
  color: #475569;
  font-size: 13px;
}

.detail-error {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 13px;
}

.detail-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 210px;
  gap: 14px;
  margin-bottom: 12px;
}

.detail-hero-main,
.detail-hero-side {
  border: 1px solid rgba(191, 219, 254, 0.9);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(239, 246, 255, 0.86));
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
}

.detail-hero-main {
  padding: 14px 16px;
}

.detail-hero-side {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 14px;
  background: linear-gradient(135deg, #f8fbff, #eef6ff);

  strong {
    color: #0f172a;
    font-size: 22px;
    line-height: 1.1;
  }

  span {
    margin-top: 5px;
    color: #1d4ed8;
    font-size: 13px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
}

.detail-hero-side__label {
  margin-bottom: 7px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.detail-eyebrow {
  margin-bottom: 3px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2563eb;
}

.detail-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.detail-formula {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: #ffffff;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;

  span {
    color: #334155;
  }

  strong {
    color: #1d4ed8;
    font-size: 14px;
  }
}

.detail-hero-note {
  margin-top: 9px;
  color: #475569;
  font-size: 12px;
  line-height: 1.45;

  b {
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }
}

.detail-chip {
  font-weight: 700 !important;
}

.detail-chip--delta {
  background: #dbeafe !important;
  color: #1d4ed8 !important;
}

.detail-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.detail-summary-card {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);

  span {
    display: block;
    margin-bottom: 4px;
    color: #64748b;
    font-size: 11px;
    font-weight: 600;
  }

  strong {
    color: #0f172a;
    font-size: 14px;
    font-variant-numeric: tabular-nums;
  }
}

.detail-summary-card--accent {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.detail-payment-breakdown {
  margin: 0 0 14px;
  padding: 14px;
  border: 1px solid #dbeafe;
  border-radius: 16px;
  background:
    linear-gradient(135deg, rgba(239, 246, 255, 0.72), rgba(255, 255, 255, 0.95)),
    #fff;
}

.detail-payment-breakdown__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 12px;
}

.detail-payment-breakdown__total {
  min-width: 180px;
  padding: 10px 12px;
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  background: #ffffff;
  text-align: right;

  span {
    display: block;
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 3px;
    color: #1d4ed8;
    font-size: 16px;
    font-variant-numeric: tabular-nums;
  }
}

.detail-payment-breakdown__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.detail-payment-item {
  padding: 11px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.88);

  span {
    display: block;
    min-height: 30px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
  }

  strong {
    display: block;
    margin-top: 6px;
    color: #0f172a;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
  }
}

.detail-payment-item--blue {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.detail-payment-item--green {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.detail-section {
  margin-top: 14px;
}

.detail-section-title {
  margin-bottom: 8px;
  color: #1e293b;
  font-size: 13px;
  font-weight: 700;
}

.detail-section-title--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.detail-section-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.detail-balance {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 9px;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.detail-clear-filter {
  min-height: 26px !important;
  padding: 0 8px !important;
  border-radius: 8px !important;
  color: #2563eb !important;
  font-size: 11px !important;
  font-weight: 700 !important;

  &:disabled {
    color: #94a3b8 !important;
  }
}

.detail-muted {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
}

.detail-reasons {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.detail-reason-card {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.07);
  }
}

.detail-reason-top {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
  color: inherit;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.01em;
}

.detail-reason-card--active {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12), 0 8px 18px rgba(15, 23, 42, 0.08);
}

.detail-reason-name {
  min-height: 34px;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.detail-reason-meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;

  strong {
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }
}

.detail-reason--changed {
  background: #fff7ed !important;
  border-color: #fed7aa !important;
  color: #9a3412 !important;
}

.detail-reason--new {
  background: #eff6ff !important;
  border-color: #bfdbfe !important;
  color: #1d4ed8 !important;
}

.detail-reason--missing {
  background: #fef2f2 !important;
  border-color: #fecaca !important;
  color: #b91c1c !important;
}

.detail-reason--docs {
  background: #f0fdf4 !important;
  border-color: #bbf7d0 !important;
  color: #15803d !important;
}

.detail-reason--default {
  background: #f8fafc !important;
  border-color: #e2e8f0 !important;
  color: #475569 !important;
}

.detail-row-filters {
  display: grid;
  grid-template-columns: 120px 120px minmax(180px, 240px) minmax(280px, 1fr);
  gap: 10px;
  margin: -2px 0 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: linear-gradient(180deg, #fbfdff 0%, #f8fafc 100%);
}

.detail-row-filter-btn {
  min-height: 38px !important;
  justify-content: space-between !important;
  padding: 0 11px !important;
  border: 1px solid #dbe6f2 !important;
  border-radius: 11px !important;
  background: #fff !important;
  color: #475569 !important;
  font-size: 12px !important;
  font-weight: 800 !important;
  box-shadow: 0 4px 12px rgba(15, 23, 42, .035) !important;

  :deep(.q-btn__content) {
    width: 100%;
    justify-content: space-between;
    overflow: hidden;
  }

  :deep(.block) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.detail-row-filter-btn--active {
  border-color: #93c5fd !important;
  background: #eff6ff !important;
  color: #1d4ed8 !important;
}

:deep(.detail-filter-menu-content) {
  min-width: 220px;
  max-width: 520px;
  max-height: 360px;
  border: 1px solid #dbe6f2;
  border-radius: 14px;
  box-shadow: 0 18px 38px rgba(15, 23, 42, .14);
  overflow: auto;
}

.detail-filter-list {
  min-width: 220px;
  background: #fff;
}

.detail-filter-option {
  min-height: 36px;
  color: #334155;
  font-size: 12px;
  font-weight: 700;

  :deep(.q-item__section--side) {
    min-width: 26px;
    padding-right: 6px;
    color: #94a3b8;
  }
}

.detail-filter-option--active {
  background: #eff6ff;
  color: #1d4ed8;

  :deep(.q-item__section--side) {
    color: #2563eb;
  }
}

.detail-filter-option--empty {
  color: #94a3b8;
  cursor: default;
}

.detail-table-wrap {
  max-height: 420px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
}

.detail-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 9px 10px;
    background: #f1f5f9;
    color: #475569;
    font-size: 11px;
    font-weight: 700;
    text-align: left;
    white-space: nowrap;
    border-bottom: 1px solid #e2e8f0;
  }

  td {
    padding: 9px 10px;
    background: #fff !important;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: top;
  }
}

.detail-empty-filter {
  padding: 22px 12px !important;
  color: #64748b !important;
  text-align: center;
  font-weight: 700;
  background: #f8fafc !important;
}

@media (max-width: 1200px) {
  .detail-row-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .detail-row-filters {
    grid-template-columns: 1fr;
  }
}

.detail-work-name {
  max-width: 520px;
  color: #0f172a;
  font-weight: 600;
  line-height: 1.35;
}

.detail-work-meta {
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.detail-volume {
  display: grid;
  gap: 4px;
  min-width: 132px;
  color: #475569;
  font-size: 11px;

  div {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 3px 7px;
    border-radius: 9px;
    background: #f8fafc;
  }

  span {
    color: #94a3b8;
    font-weight: 700;
  }

  strong {
    color: #0f172a;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
}

.detail-volume__delta {
  background: #ecfdf3 !important;

  strong {
    color: #047857;
  }
}

.detail-volume__delta--negative {
  background: #fff7ed !important;

  strong {
    color: #b45309;
  }
}

.detail-delta {
  color: #b45309;
  font-weight: 700;
}

.detail-reason-chip {
  height: 22px !important;
  font-size: 11px !important;
  font-weight: 700 !important;
}

.detail-hidden-note {
  margin-top: 8px;
}

.detail-docs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 10px;
}

.detail-doc-card {
  padding: 10px 12px;
  border: 1px solid #e0f2fe;
  border-radius: 12px;
  background: #f0f9ff;

  strong {
    display: block;
    margin-top: 6px;
    color: #075985;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }
}

.detail-doc-title {
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
}

.detail-doc-meta {
  margin-top: 2px;
  color: #64748b;
  font-size: 11px;
}

@media (max-width: 1180px) {
  .detail-hero {
    grid-template-columns: 1fr;
  }

  .detail-summary-grid,
  .detail-reasons,
  .detail-payment-breakdown__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .detail-payment-breakdown__head {
    flex-direction: column;
  }

  .detail-payment-breakdown__total {
    width: 100%;
    text-align: left;
  }

  .detail-summary-grid,
  .detail-reasons,
  .detail-payment-breakdown__grid {
    grid-template-columns: 1fr;
  }
}
</style>
